/**
 * DeviceInspectionSheet — adaptive bottom sheet to record an inspection
 * Drives UI from the shared inspection catalog (findings → actions).
 *
 * Props: open, device, serviceId, onClose, onSaved(inspection)
 */
import React, { useMemo, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, AlertTriangle, Camera, ImageIcon, Trash2, Bell, MapPin } from "lucide-react";
import api, { assetUrl } from "@/lib/api";
import { toast } from "sonner";
import {
    ACTIONS, UV_SPECIES, UV_SPECIES_MAX, UV_SPECIES_LBL,
    getFindings, getFindingByCode, isAlertTriggered,
} from "@/lib/inspectionCatalog";

const ACTIVITY_OPTS = [
    { v: "none",   l: "Sin actividad" },
    { v: "low",    l: "Baja" },
    { v: "medium", l: "Media" },
    { v: "high",   l: "Alta" },
];

const PEST_OPTS = [
    { v: "",                    l: "(Ninguna)" },
    { v: "raton",               l: "Ratón" },
    { v: "rata",                l: "Rata" },
    { v: "insectos_rastreros",  l: "Insectos rastreros" },
    { v: "insectos_voladores",  l: "Insectos voladores" },
];

const SUBTYPE_LBL = { gato_papel: "Gato papel", bandeja: "Bandeja", multicaptura: "Multicaptura", trex: "T-Rex", caja: "Cebadero de caja", tubo: "Cebadero de tubo" };
const TYPE_LBL = { cebadero_cebo: "Cebadero con cebo", trampa: "Trampa de captura", lampara_uv: "Lámpara UV" };
const ZONE_LBL = { verde: "Verde (perímetro exterior)", amarilla: "Amarilla (puntos focales interiores)", roja: "Roja (áreas de acceso al cliente)" };

export default function DeviceInspectionSheet({ open, device, serviceId, onClose, onSaved, onRelocate }) {
    const [form, setForm] = useState({
        finding_code: "",
        action_code: "",
        insect_count: 0,
        uv_insect_type: "",
        activity_level: "none",
        pest_type: "",
        notes: "",
        photo_url: "",
    });
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [existing, setExisting] = useState(null);
    const photoInputRef = useRef(null);

    const findings = useMemo(
        () => getFindings(device?.device_type, device?.trap_subtype),
        [device?.device_type, device?.trap_subtype]
    );
    const currentFinding = useMemo(
        () => getFindingByCode(device?.device_type, device?.trap_subtype, form.finding_code),
        [device?.device_type, device?.trap_subtype, form.finding_code]
    );
    const allowedActions = currentFinding?.actions || [];
    const showInsectFields = !!currentFinding?.showInsects;
    const showPhoto = !!currentFinding?.photo;
    const willTriggerAlert = isAlertTriggered(currentFinding, form.action_code);

    // Load existing inspection (if any) when device or sheet opens
    React.useEffect(() => {
        if (!open || !device || !serviceId) return;
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get(`/device-inspections?service_id=${serviceId}&device_id=${device.id}`);
                if (cancelled) return;
                if (r.data && r.data.length > 0) {
                    const ex = r.data[0];
                    setExisting(ex);
                    setForm({
                        finding_code: ex.finding_code || "",
                        action_code: ex.action_code || "",
                        insect_count: ex.insect_count || 0,
                        uv_insect_type: ex.uv_insect_type || "",
                        activity_level: ex.activity_level || "none",
                        pest_type: ex.pest_type || "",
                        notes: ex.notes || "",
                        photo_url: ex.photo_url || "",
                    });
                } else {
                    setExisting(null);
                    setForm({
                        finding_code: "",
                        action_code: "",
                        insect_count: 0,
                        uv_insect_type: "",
                        activity_level: "none",
                        pest_type: "",
                        notes: "",
                        photo_url: "",
                    });
                }
            } catch (e) {
                if (!cancelled) setExisting(null);
            }
        })();
        return () => { cancelled = true; };
    }, [open, device?.id, serviceId]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!device) return null;

    const handleFindingChange = (v) => {
        const f = getFindingByCode(device.device_type, device.trap_subtype, v);
        // Auto-select action if only one option
        const newAction = (f?.actions?.length === 1) ? f.actions[0] : "";
        setForm((s) => ({
            ...s,
            finding_code: v,
            action_code: newAction,
            // reset insect fields if no longer applicable
            insect_count: f?.showInsects ? s.insect_count : 0,
            uv_insect_type: f?.showInsects ? s.uv_insect_type : "",
        }));
    };

    const handlePhotoPick = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!existing?.id) {
            // We need an inspection id first — guide the user
            toast.info("Guarda el hallazgo primero, luego adjunta la foto.");
            e.target.value = "";
            return;
        }
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const r = await api.post(`/device-inspections/${existing.id}/photo`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setForm((s) => ({ ...s, photo_url: r.data.photo_url }));
            toast.success("Foto adjuntada");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudo subir la foto");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const handleSave = async () => {
        if (!serviceId) {
            toast.error("Falta el ID del servicio");
            return;
        }
        if (findings.length > 0 && !form.finding_code) {
            toast.error("Selecciona un hallazgo");
            return;
        }
        if (currentFinding && allowedActions.length > 0 && !form.action_code) {
            toast.error("Selecciona una acción");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                service_id: serviceId,
                device_id: device.id,
                finding_code: form.finding_code || null,
                action_code: form.action_code || null,
                activity_level: form.activity_level,
                pest_type: form.pest_type || "",
                notes: form.notes,
            };
            if (device.device_type === "lampara_uv") {
                payload.insect_count = Number(form.insect_count) || 0;
                payload.uv_insect_type = form.uv_insect_type || "";
            }
            const r = await api.post("/device-inspections", payload);
            setExisting(r.data);
            const msg = existing
                ? (willTriggerAlert ? "Inspección actualizada — se notificó al admin" : "Inspección actualizada")
                : (willTriggerAlert ? "Inspección guardada — se notificó al admin" : "Inspección guardada");
            toast.success(msg);
            onSaved?.(r.data);
            // Keep sheet open if photo prompt is shown and not yet attached
            if (!showPhoto || form.photo_url) {
                onClose?.();
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo guardar");
        } finally {
            setBusy(false);
        }
    };

    const subtypeLbl = device.trap_subtype ? SUBTYPE_LBL[device.trap_subtype] : "";
    const hasSubtype = device.device_type === "trampa" || device.device_type === "cebadero_cebo";

    return (
        <Sheet open={open} onOpenChange={(v) => !v && onClose?.()}>
            <SheetContent side="bottom" className="rounded-t-2xl max-h-[92vh] overflow-y-auto" data-testid="device-inspection-sheet">
                <SheetHeader className="text-left">
                    <SheetTitle className="text-xl">{device.device_number}</SheetTitle>
                    <SheetDescription className="text-xs">
                        Registra el hallazgo de esta inspección
                    </SheetDescription>
                </SheetHeader>

                {/* Device info block */}
                <div className="mt-3 bg-slate-50 border rounded-md p-3 text-xs space-y-1">
                    <div className="flex justify-between">
                        <span className="text-muted-foreground">Tipo:</span>
                        <span className="font-medium">{TYPE_LBL[device.device_type]}</span>
                    </div>
                    {hasSubtype && (
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Subtipo:</span>
                            <span className={`font-medium ${!subtypeLbl ? "italic text-muted-foreground" : ""}`}>
                                {subtypeLbl || "Sin especificar"}
                            </span>
                        </div>
                    )}
                    <div className="flex justify-between">
                        <span className="text-muted-foreground">Zona:</span>
                        <span className="font-medium">{ZONE_LBL[device.zone]}</span>
                    </div>
                    {device.area_name && (
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Área:</span>
                            <span className="font-medium">{device.area_name}</span>
                        </div>
                    )}
                </div>

                {/* Already inspected warning */}
                {existing && (
                    <div className="mt-3 bg-orange-50 border border-orange-300 rounded-md p-3 flex items-start gap-2" data-testid="already-inspected-warning">
                        <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                            <div className="font-semibold text-orange-900">Ya inspeccionado en esta visita</div>
                            <div className="text-xs text-orange-800 mt-0.5">
                                Esta actividad ya la registraste hoy. ¿Deseas editarla?
                            </div>
                        </div>
                    </div>
                )}

                <div className="space-y-4 mt-4 pb-4">
                    {/* WARNING for trampas without subtype */}
                    {device.device_type === "trampa" && !device.trap_subtype && (
                        <div className="bg-amber-50 border border-amber-300 text-amber-900 text-xs rounded p-2">
                            Esta trampa no tiene subtipo asignado. Pídele al admin que lo configure (T-Rex, Multicaptura, Gato papel o Bandeja) para mostrar el catálogo de hallazgos correcto.
                        </div>
                    )}

                    {/* FINDING (Estado) */}
                    {findings.length > 0 && (
                        <div>
                            <Label>Estado / Hallazgo</Label>
                            <Select value={form.finding_code} onValueChange={handleFindingChange}>
                                <SelectTrigger data-testid="ins-finding"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
                                <SelectContent>
                                    {findings.map((f) => (
                                        <SelectItem key={f.v} value={f.v} data-testid={`ins-finding-opt-${f.v}`}>
                                            {f.l}
                                            {f.alert ? " ⚠" : ""}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {/* ACTION TAKEN */}
                    {currentFinding && allowedActions.length > 0 && (
                        <div>
                            <Label>Acción tomada</Label>
                            <Select value={form.action_code} onValueChange={(v) => setForm({ ...form, action_code: v })}>
                                <SelectTrigger data-testid="ins-action"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
                                <SelectContent>
                                    {allowedActions.map((a) => (
                                        <SelectItem key={a} value={a} data-testid={`ins-action-opt-${a}`}>
                                            {ACTIONS[a] || a}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {willTriggerAlert && (
                                <div className="mt-2 flex items-start gap-1.5 bg-red-50 border border-red-200 rounded p-2 text-[11px] text-red-800" data-testid="alert-banner">
                                    <Bell className="w-3.5 h-3.5 mt-0.5 text-red-600" />
                                    <span>Esta selección <b>generará una alerta</b> al administrador.</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* UV-specific fields */}
                    {device.device_type === "lampara_uv" && showInsectFields && (
                        <div className="space-y-3" data-testid="uv-insect-fields">
                            <div>
                                <Label>N° aprox. insectos</Label>
                                <Input
                                    type="number"
                                    min={0}
                                    value={form.insect_count}
                                    onChange={(e) => setForm({ ...form, insect_count: e.target.value })}
                                    data-testid="ins-insect-count"
                                />
                            </div>
                            <div>
                                <Label>Tipos predominantes <span className="text-[11px] text-muted-foreground font-normal">(máx {UV_SPECIES_MAX})</span></Label>
                                <UVSpeciesMultiSelect
                                    value={form.uv_insect_type}
                                    onChange={(v) => setForm({ ...form, uv_insect_type: v })}
                                />
                            </div>
                        </div>
                    )}

                    {/* PHOTO (optional) */}
                    {showPhoto && (
                        <div data-testid="photo-section">
                            <Label>Foto del hallazgo (opcional)</Label>
                            <p className="text-[11px] text-muted-foreground mb-2">
                                Adjunta una foto de la trampa con insectos. Útil para evidencia y diagnóstico.
                            </p>
                            {form.photo_url ? (
                                <div className="relative inline-block" data-testid="ins-photo-preview">
                                    <img
                                        src={assetUrl(form.photo_url)}
                                        alt="Hallazgo"
                                        className="max-w-full max-h-40 rounded border"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setForm({ ...form, photo_url: "" })}
                                        className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1"
                                        title="Quitar"
                                        data-testid="ins-photo-remove"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    <input
                                        ref={photoInputRef}
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        onChange={handlePhotoPick}
                                        className="hidden"
                                        data-testid="ins-photo-input"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => photoInputRef.current?.click()}
                                        disabled={uploading || !existing}
                                        data-testid="ins-photo-button"
                                    >
                                        {uploading ? (
                                            <><ImageIcon className="w-4 h-4 mr-1.5 animate-pulse" /> Subiendo...</>
                                        ) : (
                                            <><Camera className="w-4 h-4 mr-1.5" /> Tomar / Elegir foto</>
                                        )}
                                    </Button>
                                    {!existing && (
                                        <span className="text-[11px] text-muted-foreground">
                                            Guarda el hallazgo primero para poder adjuntar foto.
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* COMMON FIELDS */}
                    <div className={`grid gap-3 ${device.device_type === "lampara_uv" ? "grid-cols-1" : "grid-cols-2"}`}>
                        <div>
                            <Label>Nivel de actividad</Label>
                            <Select value={form.activity_level} onValueChange={(v) => setForm({ ...form, activity_level: v })}>
                                <SelectTrigger data-testid="ins-activity"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {ACTIVITY_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        {device.device_type !== "lampara_uv" && (
                            <div>
                                <Label>Tipo de plaga</Label>
                                <Select value={form.pest_type || "__none__"} onValueChange={(v) => setForm({ ...form, pest_type: v === "__none__" ? "" : v })}>
                                    <SelectTrigger data-testid="ins-pest-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {PEST_OPTS.map((o) => <SelectItem key={o.v || "_x"} value={o.v || "__none__"}>{o.l}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>

                    <div>
                        <Label>Observaciones</Label>
                        <Textarea
                            rows={3}
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            placeholder="Notas libres..."
                            data-testid="ins-notes"
                        />
                    </div>
                </div>

                <SheetFooter className="sticky bottom-0 bg-white border-t pt-3 -mx-6 px-6 pb-4 flex gap-2">
                    {onRelocate && device?.device_type === "trampa" && (
                        <Button
                            type="button"
                            variant="outline"
                            onClick={async () => {
                                try {
                                    await api.post(`/pest-devices/${device.id}/rotate`);
                                    toast.success(device.rotated ? "▲ Normal" : "▼ Rotado");
                                    onSaved?.();
                                } catch (e) { toast.error("Error"); }
                            }}
                            className="border-purple-300 text-purple-700 hover:bg-purple-50"
                            data-testid="ins-rotate"
                        >
                            ↻ Rotar
                        </Button>
                    )}
                    {onRelocate && (
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onRelocate(device)}
                            className="border-blue-300 text-blue-700 hover:bg-blue-50"
                            data-testid="ins-relocate"
                            title="Reubicar este dispositivo en el plano"
                        >
                            <MapPin className="w-4 h-4 mr-1" /> Reubicar
                        </Button>
                    )}
                    <Button variant="outline" onClick={onClose} className="flex-1">Cancelar</Button>
                    <Button
                        onClick={handleSave}
                        disabled={busy}
                        className={`flex-1 ${existing ? "bg-orange-600 hover:bg-orange-700" : ""}`}
                        data-testid="ins-save"
                    >
                        <Save className="w-4 h-4 mr-1" /> {existing ? "Actualizar" : "Guardar"}
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}

/**
 * Multi-select chip group for UV predominant species (max 3).
 * Stores value as comma-separated string for backwards-compat with `uv_insect_type` field.
 */
function UVSpeciesMultiSelect({ value, onChange }) {
    const selected = (value || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

    const toggle = (code) => {
        let next;
        if (selected.includes(code)) {
            next = selected.filter((c) => c !== code);
        } else if (selected.length >= UV_SPECIES_MAX) {
            toast.error(`Máximo ${UV_SPECIES_MAX} tipos predominantes`);
            return;
        } else {
            next = [...selected, code];
        }
        onChange(next.join(","));
    };

    return (
        <div className="space-y-1.5" data-testid="ins-uv-type">
            <div className="flex flex-wrap gap-1.5">
                {UV_SPECIES.map((s) => {
                    const active = selected.includes(s.v);
                    const disabled = !active && selected.length >= UV_SPECIES_MAX;
                    return (
                        <button
                            key={s.v}
                            type="button"
                            onClick={() => toggle(s.v)}
                            disabled={disabled}
                            data-testid={`ins-uv-opt-${s.v}`}
                            data-active={active ? "true" : "false"}
                            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                                active
                                    ? "bg-[hsl(var(--trulyn-navy))] text-white border-transparent"
                                    : disabled
                                    ? "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"
                                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                            }`}
                        >
                            {s.l}
                        </button>
                    );
                })}
            </div>
            <div className="text-[10px] text-muted-foreground">
                {selected.length === 0
                    ? "Selecciona 1 a 3 tipos"
                    : `${selected.length}/${UV_SPECIES_MAX} seleccionados: ${selected.map((c) => UV_SPECIES_LBL[c] || c).join(", ")}`}
            </div>
        </div>
    );
}

